'use client';

import Link from "next/link";
import { isAdminRole } from '@/lib/auth/roles';
import { usePathname, useRouter } from "next/navigation";
import { LogOut, User, Shield, Server, Search, Bell, CreditCard, Coins, FileText, Store, Ticket, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuLink,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { settings } from "../../config/settings";
import { apiLogout } from "../helpers/auth";
import { useMemo, useState, useEffect } from "react";
import { useTranslations } from 'next-intl';

const navLinks = [
  { label: "Forums", href: "/forums" },
  { label: "Status", href: "/status" },
  { label: "Pricing", href: "/pricing" },
  { label: "Blogs", href: "/blog" },
  { label: "FAQ", href: "/faq" },
  { label: "Contact", href: "/contact-us" },
];

export default function TopNavbar({ user }) {
  const t = useTranslations('Auto.TopNavbar');
  const pathname = usePathname();
  const router = useRouter();
  const [commandOpen, setCommandOpen] = useState(false);
  const [allServers, setAllServers] = useState([]);
  const [serversLoaded, setServersLoaded] = useState(false);
  const [serverSearchLoading, setServerSearchLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  // Ctrl+K / Cmd+K shortcut for command search
  useEffect(() => {
    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setCommandOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  useEffect(() => {
    if (!user) {
      setAllServers([]);
      setServersLoaded(false);
      return;
    }

    if (!commandOpen || serversLoaded) return;

    let cancelled = false;
    setServerSearchLoading(true);

    const loadServers = async () => {
      try {
        const res = await fetch('/api/hosting/servers', { cache: 'no-store' });
        if (!res.ok) return;

        const data = await res.json();
        const rawServers = data?.servers || data?.owned || data || [];
        const normalized = Array.isArray(rawServers) ? rawServers : [];
        const uniqueByName = new Map();
        normalized.forEach((server) => {
          const name = String(server?.name || '').trim();
          if (!name || uniqueByName.has(name)) return;
          uniqueByName.set(name, server);
        });

        if (!cancelled) {
          setAllServers(Array.from(uniqueByName.values()));
          setServersLoaded(true);
        }
      } catch (e) {
        console.error('Server search failed:', e);
      } finally {
        if (!cancelled) {
          setServerSearchLoading(false);
        }
      }
    };

    loadServers();

    return () => {
      cancelled = true;
    };
  }, [commandOpen, serversLoaded, user]);

  const serverResults = useMemo(() => {
    if (!user) return [];
    const query = searchValue.trim().toLowerCase();

    if (!query) {
      return allServers.slice(0, 8);
    }

    return allServers
      .filter((server) => String(server?.name || '').toLowerCase().includes(query))
      .slice(0, 8);
  }, [allServers, searchValue, user]);

  useEffect(() => {
    if (!commandOpen) {
      setSearchValue("");
    }
  }, [commandOpen]);

  const onLogout = async () => {
    try {
      await apiLogout();
    } finally {
      router.push("/");
      router.refresh();
    }
  };

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-14 items-center px-4 sm:px-6 lg:px-8">
          {/* Left: Logo - aligned to left wall */}
          <div className="flex shrink-0">
            <Link href="/" className="flex items-center space-x-2 no-underline">
              <img
                src="/apect-logo.png"
                alt={t('apect')}
                className="h-9 w-auto object-contain"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </Link>
          </div>

          {/* Center: NavigationMenu links (desktop) */}
          <div className="hidden md:flex flex-1 justify-center">
            <NavigationMenu>
              <NavigationMenuList>
                {navLinks.map((link) => {
                  const isActive = link.href === "/"
                    ? pathname === "/"
                    : pathname.startsWith(link.href);
                  return (
                    <NavigationMenuItem key={link.href}>
                      <Link href={link.href} legacyBehavior passHref>
                        <NavigationMenuLink
                          className={cn(
                            navigationMenuTriggerStyle(),
                            isActive
                              ? "text-foreground bg-accent/50"
                              : "text-muted-foreground"
                          )}
                        >
                          {link.label}
                        </NavigationMenuLink>
                      </Link>
                    </NavigationMenuItem>
                  );
                })}
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Right: Search + Panel + User - aligned to right wall */}
          <div className="flex items-center gap-2 ml-auto shrink-0">
            {/* Search button */}
            <Button
              variant="ghost"
              size="sm"
              className="hidden sm:inline-flex items-center gap-2 text-muted-foreground border border-border hover:bg-accent rounded-full px-4"
              onClick={() => setCommandOpen(true)}
            >
              <Search className="h-4 w-4" />
              <span className="text-xs">{t('search')}</span>
              <kbd className="pointer-events-none ml-2 inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                <span className="text-xs">⌘</span>K
              </kbd>
            </Button>

            {/* Mobile search icon */}
            {user && (
              <Button
                variant="ghost"
                size="icon"
                className="sm:hidden h-8 w-8 rounded-full"
                onClick={() => setCommandOpen(true)}
              >
                <Search className="h-4 w-4" />
              </Button>
            )}

            {user && (
              <>
                <Link href="/panel" className="hidden sm:flex">
                  <Button variant="outline" size="sm" className="rounded-full px-4 gap-2">
                    <Server className="h-4 w-4" />
                    {t('panel')}
                  </Button>
                </Link>
                <Link href="/panel" className="sm:hidden">
                  <Button variant="outline" size="icon" className="h-8 w-8 rounded-full">
                    <Server className="h-4 w-4" />
                  </Button>
                </Link>
              </>
            )}

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                    <Avatar className="h-8 w-8">
                      <AvatarImage
                        src={user.avatar_path || settings.auth?.default_avatar_path}
                        alt={user.display_name || user.username}
                      />
                      <AvatarFallback>
                        {(user.display_name || user.username || "U").charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {user.display_name || user.username}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user.email || ""}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {/* Section: Profile & Notifications */}
                  <DropdownMenuGroup>
                    <DropdownMenuItem onClick={() => router.push(settings.auth?.account_path ?? "/account")}>
                      <User className="mr-2 h-4 w-4" />
                      {t('profile')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => router.push("/account?tab=notifications")}>
                      <Bell className="mr-2 h-4 w-4" />
                      {t('notifications')}
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  {/* Section: Billing, My Servers, Credits */}
                  <DropdownMenuGroup>
                    <DropdownMenuItem onClick={() => router.push("/subscriptions")}>
                      <CreditCard className="mr-2 h-4 w-4" />
                      {t('billing')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => router.push("/panel/servers")}>
                      <Server className="mr-2 h-4 w-4" />
                      {t('myServers')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => router.push("/panel/credits")}>
                      <Coins className="mr-2 h-4 w-4" />
                      {t('credits')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => router.push("/panel/stores")}>
                      <Store className="mr-2 h-4 w-4" />
                      {t('stores')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => router.push("/panel/tickets")}>
                      <Ticket className="mr-2 h-4 w-4" />
                      {t('tickets')}
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  {(user.is_admin || isAdminRole(user.access_role ?? user.role)) && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => router.push("/admin")}>
                        <Shield className="mr-2 h-4 w-4" />
                        {t('adminPanel')}
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  {/* Section: Log out */}
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={onLogout}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('logOut')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link href={settings.auth?.login_path ?? "/login"}>
                  <Button variant="ghost" size="sm">{t('logIn')}</Button>
                </Link>
                <Link href={settings.auth?.register_path ?? "/register"}>
                  <Button size="sm" className="rounded-full px-4">{t('signUp')}</Button>
                </Link>
              </div>
            )}

          </div>
        </div>
      </header>

      {/* Command search dialog */}
      <CommandDialog open={commandOpen} onOpenChange={setCommandOpen}>
        <Command>
          <CommandInput placeholder={t('searchServersPagesSettings')} value={searchValue} onValueChange={setSearchValue} />
          <CommandList>
            <CommandEmpty>{t('noResultsFound')}</CommandEmpty>
            {user && (serverSearchLoading || serverResults.length > 0) && (
              <>
                <CommandGroup heading="Servers">
                  {serverSearchLoading ? (
                    <CommandItem disabled>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('searching')}
                    </CommandItem>
                  ) : (
                    serverResults.map((srv) => (
                      <CommandItem
                        key={srv.name}
                        onSelect={() => { router.push(`/panel/server/${srv.name}/general`); setCommandOpen(false); }}
                      >
                        <Server className="mr-2 h-4 w-4" />
                        {srv.name}
                      </CommandItem>
                    ))
                  )}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup heading="Pages">
              <CommandItem onSelect={() => { router.push("/"); setCommandOpen(false); }}>
                <FileText className="mr-2 h-4 w-4" />
                {t('home')}
              </CommandItem>
              <CommandItem onSelect={() => { router.push("/forums"); setCommandOpen(false); }}>
                <FileText className="mr-2 h-4 w-4" />
                {t('forums')}
              </CommandItem>
              <CommandItem onSelect={() => { router.push("/blog"); setCommandOpen(false); }}>
                <FileText className="mr-2 h-4 w-4" />
                {t('blogs')}
              </CommandItem>
              <CommandItem onSelect={() => { router.push("/faq"); setCommandOpen(false); }}>
                <FileText className="mr-2 h-4 w-4" />
                {t('faq')}
              </CommandItem>
              <CommandItem onSelect={() => { router.push("/status"); setCommandOpen(false); }}>
                <FileText className="mr-2 h-4 w-4" />
                {t('serverStatus')}
              </CommandItem>
              <CommandItem onSelect={() => { router.push("/contact-us"); setCommandOpen(false); }}>
                <FileText className="mr-2 h-4 w-4" />
                {t('contactUs')}
              </CommandItem>
            </CommandGroup>
            {user && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Panel">
                  <CommandItem onSelect={() => { router.push("/panel"); setCommandOpen(false); }}>
                    <Server className="mr-2 h-4 w-4" />
                    {t('dashboard')}
                  </CommandItem>
                  <CommandItem onSelect={() => { router.push("/panel/servers"); setCommandOpen(false); }}>
                    <Server className="mr-2 h-4 w-4" />
                    {t('myServers')}
                  </CommandItem>
                  <CommandItem onSelect={() => { router.push("/create"); setCommandOpen(false); }}>
                    <Server className="mr-2 h-4 w-4" />
                    {t('createServer')}
                  </CommandItem>
                  <CommandItem onSelect={() => { router.push("/panel/credits"); setCommandOpen(false); }}>
                    <Coins className="mr-2 h-4 w-4" />
                    {t('creditStore')}
                  </CommandItem>
                  <CommandItem onSelect={() => { router.push("/panel/stores"); setCommandOpen(false); }}>
                    <Store className="mr-2 h-4 w-4" />
                    {t('stores')}
                  </CommandItem>
                  <CommandItem onSelect={() => { router.push("/panel/tickets"); setCommandOpen(false); }}>
                    <Ticket className="mr-2 h-4 w-4" />
                    {t('tickets')}
                  </CommandItem>
                  <CommandItem onSelect={() => { router.push("/subscriptions"); setCommandOpen(false); }}>
                    <CreditCard className="mr-2 h-4 w-4" />
                    {t('subscriptions')}
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
                <CommandGroup heading="Account">
                  <CommandItem onSelect={() => { router.push("/account"); setCommandOpen(false); }}>
                    <User className="mr-2 h-4 w-4" />
                    {t('profileSettings')}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  );
}

